/**
 * Turn Light Mode — Hook + provider de gestion du thème backoffice.
 *
 * Contrat :
 * - localStorage `werko-theme` : "dark" | "light" (source de vérité)
 * - 1er visit (pas de storage) : DARK strict par défaut. La préférence
 *   système est volontairement ignorée — le light est un opt-in explicite
 *   via le toggle sidebar. Voir DECISIONS.md § "Mode par défaut au 1er visit".
 * - Bascule → toggle la classe `dark` / `light` sur `<html>`.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "werko-theme";

const ThemeContext = createContext({ theme: "dark", setTheme: () => {}, toggleTheme: () => {} });

function _initialTheme() {
  if (typeof window === "undefined") return "dark";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "dark" || stored === "light") return stored;
  } catch (_) { /* private mode / storage disabled */ }
  // Défaut STRICT = dark. La préférence système est volontairement ignorée :
  // les users existants n'ont jamais toggle → doivent rester sur l'UX Werko
  // sombre qu'ils connaissent. Le light est un opt-in explicite via le toggle
  // sidebar. Voir DECISIONS.md § "Mode par défaut au 1er visit".
  return "dark";
}

function _applyTheme(theme) {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  html.classList.remove("dark", "light");
  html.classList.add(theme);
  html.setAttribute("data-theme", theme);
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(_initialTheme);

  // Applique la classe au mount + à chaque changement
  useEffect(() => {
    _applyTheme(theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch (_) { /* ignore */ }
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (next !== "dark" && next !== "light") return;
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

// Application immédiate au chargement (avant React mount) — évite le flash blanc.
// Doit être importé/exécuté dans index.js AVANT tout render.
export function bootTheme() {
  _applyTheme(_initialTheme());
}
